
import React, { useState, useEffect, useRef } from 'react';
import { 
  Cpu, 
  Layout, 
  History, 
  LineChart, 
  Network, 
  Zap, 
  Users, 
  Terminal, 
  MessageCircle, 
  ShieldCheck, 
  Command,
  Sparkles
} from 'lucide-react';

interface AccordionItem {
  id: number;
  title: string;
  userQuestion: string;
  chatResponse: string;
  description: string;
  visualType: 'signal' | 'insight' | 'scenario' | 'timeline' | 'action' | 'feedback';
}

const accordionItems: AccordionItem[] = [
  {
    id: 1,
    title: "Unified Signal Center",
    userQuestion: "What’s actually happening right now?",
    chatResponse: "Here is a unified operational view of all food-product demand signals across 40+ SKUs and multiple factories, continuously updated from your ERP and client portals.",
    description: "Consolidates operational signals across systems, workflows, and teams into a single, continuously updated operational view.",
    visualType: 'signal'
  },
  {
    id: 2,
    title: "Cross-System Intelligence Hub",
    userQuestion: "What’s connected that we’re not seeing?",
    chatResponse: "Analysis reveals these SKUs, clients, and factories are interdependent. A campaign-driven spike from Client X is silently overloading specific factories. This inventory pressure is caused by hidden cross-factory dependencies, not just forecasting variance.",
    description: "Reveals hidden dependencies and cross-system impacts across processes, teams, and decisions—exposing blind spots before they escalate.",
    visualType: 'insight'
  },
  {
    id: 3,
    title: "Predictive Scenario Engine",
    userQuestion: "If we act now, what happens next?",
    chatResponse: "Scenario Mode: If Client X increases orders by 15%, Factory B will exceed capacity in 10 days. I have modeled alternative scenarios for demand reduction and promotion-driven spikes to optimize factory load balancing.",
    description: "Simulates future outcomes based on real operational data, allowing teams to test decisions and anticipate downstream effects before committing.",
    visualType: 'scenario'
  },
  {
    id: 4,
    title: "Execution Drift Timeline",
    userQuestion: "Where did execution start to go wrong?",
    chatResponse: "Drift detected: This demand variance was detectable 14 days earlier than manual logs recorded. Patterns in client adjustments show this wasn't a surprise—it was a detectable shift that started two weeks ago.",
    description: "Tracks how execution diverges from intent over time, pinpointing when, where, and why outcomes begin to drift.",
    visualType: 'timeline'
  },
  {
    id: 5,
    title: "Prescriptive Action Engine",
    userQuestion: "What should we do about it — now?",
    chatResponse: "Prioritized Actions: 1. Adjust production schedules before overload triggers. 2. Rebalance specific factory loads to absorb Client X spikes. 3. Modify procurement timing to eliminate projected raw material waste.",
    description: "Recommends prioritized actions and interventions, grounded in system-wide context, trade-offs, and predicted impact.",
    visualType: 'action'
  },
  {
    id: 6,
    title: "Execution Alignment Console",
    userQuestion: "How do we align people, decisions, and action?",
    chatResponse: "Planners, Procurement, and Production are now aligned in a shared operational space. We have eliminated spreadsheet debates by validating all assumptions against the same predictive truth.",
    description: "Provides a shared decision space where teams can question, validate, and align actions through real-time intelligence and conversational AI.",
    visualType: 'feedback'
  }
];

const TypewriterText = ({ text, onComplete, speed = 12 }: { text: string; onComplete?: () => void; speed?: number }) => {
  const [displayedText, setDisplayedText] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    setDisplayedText('');
    indexRef.current = 0;
    
    const interval = setInterval(() => {
      if (indexRef.current < text.length) {
        setDisplayedText(text.substring(0, indexRef.current + 1));
        indexRef.current++;
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span>{displayedText}</span>
  );
};

const IntelligenceChatUI = ({ item }: { item: AccordionItem }) => {
  const [showResponse, setShowResponse] = useState(false);
  const [responseStarted, setResponseStarted] = useState(false);

  useEffect(() => {
    setShowResponse(false);
    setResponseStarted(false);
  }, [item]);

  return (
    <div className="w-full h-full bg-[#0B1222] rounded-[2.5rem] overflow-hidden border border-white/5 flex flex-col shadow-2xl relative">
      {/* Top Header Bar */}
      <div className="h-14 border-b border-white/5 bg-white/5 backdrop-blur-md flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/20"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/20"></div>
          </div>
          <div className="h-4 w-px bg-white/10 mx-2"></div>
          <div className="flex items-center gap-2">
            <Command className="w-3.5 h-3.5 text-accent/50" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Groflex Command Interface</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1 bg-accent/10 rounded-full border border-accent/20">
            <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></div>
            <span className="text-[9px] font-black text-accent uppercase tracking-widest">Live Reasoning</span>
          </div>
        </div>
      </div>

      {/* Main Chat Display */}
      <div className="flex-grow p-8 overflow-y-auto no-scrollbar space-y-8 flex flex-col">
        {/* User Message */}
        <div className="flex flex-col items-end animate-slide-up">
          <div className="max-w-[85%] bg-accent rounded-2xl rounded-tr-none px-6 py-4 shadow-lg shadow-accent/10">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50 mb-1">Operator Inquiry</p>
            <div className="text-white text-base md:text-lg font-bold tracking-tight">
              <TypewriterText 
                text={item.userQuestion} 
                onComplete={() => {
                  setTimeout(() => setResponseStarted(true), 400);
                }} 
              />
              <span className="inline-block w-2 h-5 bg-white/50 ml-1 animate-pulse align-middle"></span>
            </div>
          </div>
        </div>

        {/* AI Response */}
        {responseStarted && (
          <div className="flex flex-col items-start animate-slide-up">
            <div className="max-w-[90%] bg-white/5 border border-white/10 rounded-2xl rounded-tl-none px-6 py-5 backdrop-blur-sm relative group">
              <div className="absolute -left-3 top-0 w-8 h-8 rounded-full bg-zodiac border border-white/10 flex items-center justify-center -translate-x-full">
                <Sparkles className="w-4 h-4 text-accent" />
              </div>
              
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent/60 mb-3">Intelligence Response</p>
              <div className="space-y-4">
                <div className="text-white/80 text-[15px] leading-relaxed font-medium min-h-[4rem]">
                  <TypewriterText 
                    text={item.chatResponse} 
                    speed={8}
                    onComplete={() => setShowResponse(true)}
                  />
                  {!showResponse && <span className="inline-block w-2 h-4 bg-accent/50 ml-1 animate-pulse align-middle"></span>}
                </div>
              </div>

              {/* Status Indicators (Shown after response completes) */}
              <div className={`mt-6 flex items-center gap-4 border-t border-white/5 pt-4 transition-all duration-700 ${showResponse ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                 <div className="flex items-center gap-1.5">
                   <ShieldCheck className="w-3.5 h-3.5 text-green-500/50" />
                   <span className="text-[9px] font-black uppercase tracking-widest text-white/20">Governed</span>
                 </div>
                 <div className="flex items-center gap-1.5">
                   <Zap className="w-3.5 h-3.5 text-accent/50" />
                   <span className="text-[9px] font-black uppercase tracking-widest text-white/20">Deterministic</span>
                 </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Placeholder UI */}
      <div className="h-20 border-t border-white/5 bg-white/[0.02] flex items-center px-8 shrink-0">
        <div className="flex-grow flex items-center gap-4 bg-white/5 rounded-xl px-4 py-2 border border-white/10">
          <Terminal className="w-4 h-4 text-white/20" />
          <div className="w-px h-4 bg-white/10"></div>
          <span className="text-xs text-white/10 font-medium tracking-widest uppercase">Awaiting further instruction...</span>
        </div>
      </div>
    </div>
  );
};

export const PlatformGrid: React.FC = () => {
  const [activeId, setActiveId] = useState(1);
  const activeItem = accordionItems.find(i => i.id === activeId)!;

  return (
    <section id="platform-grid" className="py-40 px-6 md:px-12 bg-brandBg overflow-hidden border-t border-zodiac/5">
      <div className="max-w-7xl mx-auto">
        <div className="mb-24 text-left">
          <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent mb-6">THE GROFLEX EXPERIENCE</p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac mb-4">Interface for Intelligent Action</h2>
          <p className="text-xl text-zodiac/50 font-light max-w-3xl leading-relaxed">
            A continuous, decision-to-execution workspace built for operational leaders.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          
          {/* LEFT: ACCORDION LIST */}
          <div className="lg:col-span-5 space-y-2">
            {accordionItems.map((item) => {
              const isActive = activeId === item.id;
              return (
                <div 
                  key={item.id} 
                  className={`border-b border-zodiac/10 last:border-0 transition-all duration-500 cursor-pointer group py-6 ${isActive ? 'opacity-100' : 'opacity-40 hover:opacity-60'}`}
                  onClick={() => setActiveId(item.id)}
                >
                  <div className="flex items-start gap-6">
                    <span className={`text-[11px] font-black tracking-widest mt-1 transition-colors duration-500 ${isActive ? 'text-accent' : 'text-zodiac/30'}`}>
                      0{item.id}
                    </span>
                    <div className="flex-grow">
                      <h3 className={`text-lg md:text-xl font-bold tracking-tight mb-2 transition-executive ${isActive ? 'text-zodiac' : 'text-zodiac'}`}>
                        {item.title}
                      </h3>
                      <div className={`overflow-hidden transition-all duration-700 ease-in-out ${isActive ? 'max-h-60 opacity-100 pb-2' : 'max-h-0 opacity-0'}`}>
                        <p className="text-sm md:text-base text-zodiac/60 font-medium leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: INTELLIGENCE CHAT STAGE */}
          <div className="lg:col-span-7 lg:sticky lg:top-32">
            <div className="relative aspect-[4/4] md:aspect-[4/3] w-full">
              <IntelligenceChatUI item={activeItem} />
              
              {/* Bottom Branding */}
              <div className="mt-8 flex items-center justify-center px-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-zodiac flex items-center justify-center text-white">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-zodiac">Groflex: Chat with AI</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
